import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import { createKassaTransaction } from "../../services/kassa/kassaServices";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import BalanceBoxes from "../../components/BalanceBoxes";
import { formatDate } from "../../utils/toDate";
import { getPaymentCode } from "../../services/PaymentCodeService";
import TextDiv from "../../components/TextDiv";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";

const KassaWithdrawModal = ({
  kassaId,
  handleToggleModal,
  refetchData,
  currentBalance,
}) => {
  const { t } = useTranslation("general");

  const [formData, setFormData] = useState({
    amount: "",
    date: new Date(),
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState("");
  const [transactionCode, setTransactionCode] = useState("");

  const validateForm = () => {
    let newErrors = "";
    const amount = parseFloat(formData.amount);

    if (!formData.amount || amount <= 0) {
      newErrors = t("amountRequired");
    } else if (formData.amount > currentBalance) {
      newErrors = t("notEnoughBalance");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const getTransCode = async () => {
    try {
      const response = await getPaymentCode();
      if (response.status == 200) {
        setTransactionCode(response?.data.code);
        return response?.data.code;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const transactionData = {
        kassaId: kassaId,
        amount: parseFloat(formData.amount),
        type: "DIRECTWITHDRAWAL",
        date: formData.date,
        notes: formData.notes || undefined,
        receiptNumber: transactionCode,
      };

      const response = await createKassaTransaction(transactionData);

      if (response.status === 201) {
        handleToggleModal();
        refetchData();
      }
    } catch (error) {
      console.error("Error creating withdrawal transaction:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleToggleModal();
  };

  useEffect(() => {
    getTransCode();
  }, []);

  return (
    <Modal
      modalSize="c-modal--small"
      title={t("withdrawal")}
      onClose={handleToggleModal}
      footer={
        <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
          <CustomButton
            variant="blank"
            title={t("cancel")}
            onClick={handleCancel}
            icon={closeIcon}
            iconSize={14}
            disabled={loading}
          />
          <CustomButton
            title={t("save")}
            onClick={handleSubmit}
            disabled={loading}
            icon={saveIcon}
            iconSize={14}
          />
        </div>
      }
      error={errors}
    >
      <div className="o-column o-column--gap-size_300">
        <div className="o-column o-column--gap-size_500">
          <div className="o-row">
            <p className="t-bold_semi-bold u-width-specific_100 u-margin_0">
              {t("receiptNumber")}:
            </p>
            <label className="t-bold_semi-bold u-margin_0">
              {transactionCode}
            </label>
          </div>

          <div className="o-row o-row--vertical-align_center">
            <label className="t-bold_semi-bold u-width-specific_100 u-margin_0">
              {t("amount")}:
            </label>
            <CustomInputWithUnit
              name="amount"
              type="moneyV2"
              value={formData.amount}
              onChange={handleInputChange}
              unit={"IQD"}
              thick
              flex
              error={errors.amount}
            />
          </div>

          <div className="o-row o-row--vertical-align_center">
            <label className="t-bold_semi-bold u-width-specific_100 u-margin_0">
              {t("Date")}:
            </label>
            <CustomInput
              name="date"
              type="date"
              value={formatDate(formData.date)}
              onChange={handleInputChange}
              thick
              flex
              error={errors.date}
            />
          </div>

          <div className="o-column o-column--gap-size_200">
            <label className="t-bold_semi-bold">{t("notes")}:</label>
            <CustomInput
              name="notes"
              type="textarea"
              value={formData.notes}
              onChange={handleInputChange}
              thick
              flex
              rows={3}
            />
          </div>
        </div>

        {currentBalance !== undefined && (
          <div className="o-row o-row--vertical-align_center o-row--gap-size_200">
            <BalanceBoxes
              boxes={[
                {
                  title: "availableBalance",
                  value: currentBalance,
                  currency: t("IQD"),
                },
              ]}
            />
          </div>
        )}

        {loading && (
          <div className="o-row o-row--horizontal-align_center">
            <p className="t-text-small">{t("processing")}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default KassaWithdrawModal;
